import { SensorType, getFirmwareApiGateway } from 'common';

import { useAppSync } from '../../contexts/AppSyncContext';
import {
  isFirmwareOverrideEnabled,
  getFirmwareOverrides,
  createMockAsyncState,
  shouldOverrideEndpoint,
  type FirmwareOverrides,
} from '../../utils/firmwareConfig';
import { HttpClient } from '../../utils/httpClient';
import { useAsyncState } from '../../utils/useAsyncState';

export interface Temperature {
  temperature: number;
  unit: string;
}

export interface Fan {
  fan_speed: number;
}

export interface Occupancy {
  occupied: boolean;
}

export interface OccupancyLastUpdated {
  last_updated: string;
}

export interface BacklightColor {
  color: 'White' | 'Red' | 'Yellow' | 'Green' | 'Blue';
  custom_rgb: { r: number; g: number; b: number };
}

export interface BacklightPattern {
  pattern: 'Off' | 'Solid' | 'Slow Pulse';
}

export interface ScreenPower {
  power: boolean;
}

interface UseDisplayControlsOptions {
  isOverride?: boolean;
  customOverrides?: FirmwareOverrides;
}

const TEMPERATURE_SENSOR_READER = SensorType.HVAC;

export function useDisplayControls(options?: UseDisplayControlsOptions) {
  const httpClient = new HttpClient(getFirmwareApiGateway());
  const { sensors } = useAppSync();
  const sensorId = sensors.find((sensor) => sensor.type === TEMPERATURE_SENSOR_READER)?.id;

  // Check if build-time override is enabled or manual override is requested
  const isOverrideMode = options?.isOverride || isFirmwareOverrideEnabled();
  const overrides = options?.customOverrides || getFirmwareOverrides();

  const backlightColor =
    isOverrideMode && shouldOverrideEndpoint('/backlight/color') && overrides.backlightColor
      ? createMockAsyncState(overrides.backlightColor)
      : useAsyncState(() => httpClient.get<BacklightColor>('/backlight/color'));

  const backlightPattern =
    isOverrideMode && shouldOverrideEndpoint('/backlight/pattern') && overrides.backlightPattern
      ? createMockAsyncState(overrides.backlightPattern)
      : useAsyncState(() => httpClient.get<BacklightPattern>('/backlight/pattern'));

  const screenPower =
    isOverrideMode && shouldOverrideEndpoint('/screen-power') && overrides.screenPower
      ? createMockAsyncState(overrides.screenPower)
      : useAsyncState(() => httpClient.get<ScreenPower>('/screen-power'));

  const temperature =
    isOverrideMode && shouldOverrideEndpoint('/temperature') && overrides.temperature
      ? createMockAsyncState(overrides.temperature)
      : useAsyncState(() => {
          if (!sensorId) {
            throw new Error(`Sensor with type "${TEMPERATURE_SENSOR_READER}" not found.`);
          }
          return httpClient.get<Temperature>(`/sensors/${sensorId}/temperature`);
        });

  const fan =
    isOverrideMode && shouldOverrideEndpoint('/fan') && overrides.fan
      ? createMockAsyncState(overrides.fan)
      : useAsyncState(() => httpClient.get<Fan>('/fan'));

  const occupancy =
    isOverrideMode && shouldOverrideEndpoint('/occupancy') && overrides.occupancy
      ? createMockAsyncState(overrides.occupancy)
      : useAsyncState(() => httpClient.get<Occupancy>('/occupancy'));

  const occupancyLastUpdated =
    isOverrideMode &&
    shouldOverrideEndpoint('/occupancy/last-updated') &&
    overrides.occupancyLastUpdated
      ? createMockAsyncState(overrides.occupancyLastUpdated)
      : useAsyncState(() => httpClient.get<OccupancyLastUpdated>('/occupancy/last-updated'));

  const refreshAll = async () => {
    const promises: Promise<void>[] = [];

    if (!isOverrideMode || !shouldOverrideEndpoint('/backlight/color')) {
      promises.push(backlightColor.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/backlight/pattern')) {
      promises.push(backlightPattern.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/screen-power')) {
      promises.push(screenPower.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/temperature')) {
      promises.push(temperature.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/fan')) {
      promises.push(fan.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/occupancy')) {
      promises.push(occupancy.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/occupancy/last-updated')) {
      promises.push(occupancyLastUpdated.execute());
    }

    await Promise.all(promises);
  };

  return {
    backlightColor,
    backlightPattern,
    screenPower,
    temperature,
    fan,
    occupancy,
    occupancyLastUpdated,
    refreshAll,
  };
}
